import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Paper,
  Grid,
  Alert,
} from '@mui/material';
import { TIMEZONES } from '../../constants/timezones';

interface ExpertMeetingSettings {
  timezone: string;
  workingHours: {
    start: string;
    end: string;
    daysOfWeek: number[];
  }[];
}

interface ExpertMeetingSettingsProps {
  onSave: (settings: ExpertMeetingSettings) => void;
  initialSettings?: ExpertMeetingSettings;
}

const defaultWorkingHours = {
  start: '09:00',
  end: '17:00',
  daysOfWeek: [1, 2, 3, 4, 5], // Monday to Friday
};

const ExpertMeetingSettings: React.FC<ExpertMeetingSettingsProps> = ({
  onSave,
  initialSettings,
}) => {
  const [settings, setSettings] = useState<ExpertMeetingSettings>({
    timezone: initialSettings?.timezone || 'Australia/Melbourne',
    workingHours: initialSettings?.workingHours || [defaultWorkingHours],
  });
  const [saveStatus, setSaveStatus] = useState<'success' | 'error' | null>(null);

  const handleSave = async () => {
    try {
      await onSave(settings);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, maxWidth: 800, margin: '0 auto' }}>
      <Typography variant="h5" gutterBottom>
        Meeting Settings
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="timezone-label">Your Timezone</InputLabel>
            <Select
              labelId="timezone-label"
              value={settings.timezone}
              label="Your Timezone"
              onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
            >
              {Object.entries(TIMEZONES)
                .sort(([, a], [, b]) => a.localeCompare(b))
                .map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Working Hours
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Your working hours will be displayed in your selected timezone.
          </Typography>
          
          {/* We can add working hours editor here in the future */}
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
            >
              Save Settings
            </Button>
          </Box>
        </Grid>

        {saveStatus && (
          <Grid item xs={12}>
            <Alert severity={saveStatus}>
              {saveStatus === 'success'
                ? 'Settings saved successfully!'
                : 'Failed to save settings. Please try again.'}
            </Alert>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default ExpertMeetingSettings;
